// web/src/pages/admin/PaymentGatewaysPage.tsx
//
// I-13 — Admin CRUD for payment-gateway codes (`/api/admin/payment-gateways`).
//
// Foundation rows ({ Manual }) are displayed with a lock indicator and have
// their delete button disabled. The backend's Password field is write-only —
// the DTO never returns it; users enter a new password only when they want to
// change it (leave blank to keep existing).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Lock } from "lucide-react";
import { z } from "zod";
import {
  listPaymentGateways,
  createPaymentGateway,
  updatePaymentGateway,
  deletePaymentGateway,
  type PaymentGatewayDto,
} from "@/lib/invoices-payments";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// Mirror of foundation payment gateways in the backend.
// Keep in sync with the backend's PaymentGatewayCodes foundation set.
export const FOUNDATION_PAYMENT_GATEWAYS: ReadonlySet<string> = new Set([
  "Manual",
]);

const schema = z.object({
  paymentGatewayCode: z.string().min(1).max(40),
  description: z.string().min(1).max(100),
  accountId: z.string().max(100).nullable(),
  // Write-only: empty string means "keep existing". Never read back from API.
  password: z.string().nullable(),
  active: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function PaymentGatewaysPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PaymentGatewayDto | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ["payment-gateways"],
    queryFn: () => listPaymentGateways(),
  });

  const upsert = useMutation({
    mutationFn: (form: Form) =>
      editing
        ? updatePaymentGateway(editing.paymentGatewayCode, {
            description: form.description,
            accountId: form.accountId,
            active: form.active,
          })
        : createPaymentGateway({
            paymentGatewayCode: form.paymentGatewayCode,
            description: form.description,
            accountId: form.accountId,
            active: form.active,
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payment-gateways"] });
      setEditing(null);
      setCreating(false);
    },
  });

  const del = useMutation({
    mutationFn: (code: string) => deletePaymentGateway(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["payment-gateways"] }),
  });

  const cols: DataGridColumn<PaymentGatewayDto>[] = [
    {
      id: "code",
      header: "Code",
      accessor: (r) => (
        <span className="inline-flex items-center gap-1">
          {FOUNDATION_PAYMENT_GATEWAYS.has(r.paymentGatewayCode) && (
            <Lock aria-label="Foundation row (delete locked)" className="size-3 text-muted-foreground" />
          )}
          {r.paymentGatewayCode}
        </span>
      ),
    },
    { id: "desc",   header: "Description", accessor: (r) => r.description },
    { id: "acct",   header: "Account ID",  accessor: (r) => r.accountId ?? "—" },
    { id: "active", header: "Active",      accessor: (r) => r.active ? "Yes" : "—" },
    {
      id: "actions",
      header: "",
      accessor: (r) => {
        const isFoundation = FOUNDATION_PAYMENT_GATEWAYS.has(r.paymentGatewayCode);
        return (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Edit ${r.paymentGatewayCode}`}
              onClick={() => setEditing(r)}
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Delete ${r.paymentGatewayCode}`}
              disabled={isFoundation || del.isPending}
              onClick={() => {
                if (!isFoundation) del.mutate(r.paymentGatewayCode);
              }}
            >
              Delete
            </Button>
          </div>
        );
      },
      align: "right",
    },
  ];

  const drawerOpen = creating || editing !== null;
  const defaultValues: Form = editing
    ? {
        paymentGatewayCode: editing.paymentGatewayCode,
        description: editing.description,
        accountId: editing.accountId,
        password: "",
        active: editing.active,
      }
    : { paymentGatewayCode: "", description: "", accountId: null, password: "", active: true };

  return (
    <ListPage backTo="/admin"
      title="Payment gateways"
      primaryAction={
        <IntentButton onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Payment Gateway</IntentButton>
      }
    >
      <DataGrid
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.paymentGatewayCode}
        totalRows={list.data?.length ?? 0}
        pageIndex={0}
        pageSize={Math.max(50, list.data?.length ?? 0)}
        loading={list.isLoading}
        onPageChange={() => undefined}
      />
      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={editing ? "Edit payment gateway" : "New payment gateway"}
      >
        <EntityForm<Form>
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div>
                <Label htmlFor="pg-code">Code</Label>
                <Input id="pg-code" disabled={editing !== null} {...form.register("paymentGatewayCode")} />
              </div>
              <div>
                <Label htmlFor="pg-desc">Description</Label>
                <Input id="pg-desc" {...form.register("description")} />
              </div>
              <div>
                <Label htmlFor="pg-acct">Account ID</Label>
                <Input
                  id="pg-acct"
                  {...form.register("accountId", {
                    setValueAs: (v: string) => (v === "" ? null : v),
                  })}
                />
              </div>
              <div>
                <Label htmlFor="pg-pwd">Password (leave blank to keep existing)</Label>
                <Input
                  id="pg-pwd"
                  type="password"
                  {...form.register("password", {
                    setValueAs: (v: string) => (v === "" ? null : v),
                  })}
                />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("active")} /> Active
              </label>
              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
